import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, saveMobileToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = loading
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        setError(null);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            // On native (Capacitor) the login response body carries the
            // access_token which we must persist ourselves — cookies aren't
            // usable across mobile origins.
            if (data?.access_token) await saveMobileToken(data.access_token);
            setUser(data);
            return { ok: true };
        } catch (e) {
            setError(formatApiError(e));
            return { ok: false, error: formatApiError(e) };
        }
    };

    const logout = async ({ everywhere = false } = {}) => {
        try {
            await api.post(`/auth/logout${everywhere ? "?all=true" : ""}`);
        } catch {
            /* noop */
        }
        await saveMobileToken(null);
        setUser(null);
    };

    const changePassword = async (new_password) => {
        try {
            await api.post("/auth/change-password", { new_password });
            // Server bumps token_version → next request will 401 anyway,
            // but reset the local state so the UI redirects cleanly.
            await saveMobileToken(null);
            setUser(null);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    return (
        <AuthContext.Provider value={{ user, error, login, logout, refresh, setError, changePassword }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
